import React, { useState, useMemo } from 'react';
import productsData from '../data/products.json';
import { ProductQuickView } from '../component/ProductQuickView';

export const JeansCollection = () => {

    const styles = `
    .industrial-overlay-mini {
            background: linear-gradient(rgba(18, 18, 20, 0.9), rgba(18, 18, 20, 0.9)), url('https://images.unsplash.com/photo-1555529771-835f59fc5efe?auto=format&fit=crop&q=80&w=2000');
            background-size: cover;
            background-position: center;
        }
        .material-symbols-outlined {
            font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
        .catalog-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        @media (min-width: 1024px) {
            .catalog-grid {
                grid-template-columns: repeat(5, minmax(0, 1fr));
            }
        }
        @media (min-width: 1536px) {
            .catalog-grid {
                grid-template-columns: repeat(6, minmax(0, 1fr));
            }
        }
`;

    // State
    const [selectedStyle, setSelectedStyle] = useState('All Styles');
    const [selectedFabric, setSelectedFabric] = useState('All Fabrics');
    const [sortBy, setSortBy] = useState('Newest First');
    const [visibleCount, setVisibleCount] = useState(10);
    const [quickViewProduct, setQuickViewProduct] = useState(null);

    const formatFabricKey = (key) => key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const styleOptions = ['All Styles', ...Object.values(productsData.attributes.fits)];
    const fabricOptions = ['All Fabrics', ...Object.keys(productsData.attributes.fabrics).map(formatFabricKey)];

    // Filter and Sort Logic
    const displayedProducts = useMemo(() => {
        let filtered = [...productsData.products];

        if (selectedStyle !== 'All Styles') {
            filtered = filtered.filter(p => productsData.attributes.fits[p.fitId] === selectedStyle);
        }

        if (selectedFabric !== 'All Fabrics') {
            filtered = filtered.filter(p => formatFabricKey(p.fabricId) === selectedFabric);
        }

        if (sortBy === 'Newest First') {
            filtered.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
        } else if (sortBy === 'Price: Low to High') {
            filtered.sort((a, b) => a.price - b.price);
        } else if (sortBy === 'Price: High to Low') {
            filtered.sort((a, b) => b.price - a.price);
        }

        return filtered;
    }, [selectedStyle, selectedFabric, sortBy]);

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    return (
        <>
            <style type="text/tailwindcss">{styles}</style>

            <section className="relative h-32 flex items-center overflow-hidden border-b border-slate-200 dark:border-slate-800">
                <div className="absolute inset-0 industrial-overlay-mini"></div>
                <div className="relative max-w-[1600px] mx-auto px-4 w-full">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white uppercase leading-tight">
                                Premium <span className="text-primary">Manufacturing</span>
                            </h1>
                            <p className="text-slate-400 text-xs font-light max-w-lg">
                                Om Clothing Co. High-grade denim production &amp; industrial technology since 2007.
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                className="bg-primary/20 hover:bg-primary/30 text-primary border border-primary/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all">Explore
                                Collection</button>
                            <button
                                className="border border-white/20 text-white/70 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-white/5 transition-all">Our
                                Process</button>
                        </div>
                    </div>
                </div>
            </section>

            <main className="max-w-[1600px] mx-auto flex flex-col md:flex-row min-h-screen">
                <aside
                    className="w-full md:w-64 border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-zinc-900/50 p-6 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
                    <div className="mb-8">
                        <h2
                            className="text-sm font-display font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-6">
                            Style Filters</h2>
                        <div className="flex flex-col gap-3">
                            {styleOptions.map(style => (
                                <label key={style} className="flex items-center gap-3 cursor-pointer group">
                                    <input 
                                        checked={selectedStyle === style}
                                        onChange={() => setSelectedStyle(style)}
                                        className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                                        type="checkbox" />
                                    <span className={`text-xs uppercase tracking-tighter transition-colors ${selectedStyle === style ? 'font-semibold text-primary' : 'font-medium text-slate-500 group-hover:text-primary'}`}>
                                        {style}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h2
                            className="text-sm font-display font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-6">
                            Fabric</h2>
                        <div className="flex flex-col gap-2">
                            {fabricOptions.map(fabric => (
                                <button 
                                    key={fabric}
                                    onClick={() => setSelectedFabric(fabric)}
                                    className={`text-left text-[10px] font-bold uppercase transition-colors ${selectedFabric === fabric ? 'text-primary' : 'text-slate-500 hover:text-primary'}`}>
                                    {fabric}
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>
                <section className="flex-1 p-4 md:p-8 bg-white dark:bg-[#0a0a0b]">
                    <div className="flex items-baseline justify-between mb-8 border-b border-slate-100 dark:border-slate-800 pb-4">
                        <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-slate-900 dark:text-white">
                            Women's <span className="text-primary">Jeans</span> <span
                                className="ml-2 text-xs font-normal text-slate-400 lowercase italic">({displayedProducts.length} styles)</span>
                        </h2>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            <span>Sort by:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="bg-transparent border-none text-[10px] font-bold uppercase text-slate-900 dark:text-white focus:ring-0 cursor-pointer">
                                <option>Newest First</option>
                                <option>Price: Low to High</option>
                                <option>Price: High to Low</option>
                            </select>
                        </div>
                    </div>
                    
                    {displayedProducts.length === 0 ? (
                        <div className="py-12 text-center text-slate-500 font-medium">
                            No products match the selected filters.
                        </div>
                    ) : (
                        <div className="catalog-grid gap-4 md:gap-6">
                            {displayedProducts.slice(0, visibleCount).map(product => {
                                const mainImage = product.variants[0]?.images[0]?.src || '';
                                const styleBadge = productsData.attributes.fits[product.fitId] || 'Style';
                                
                                return (
                                    <div 
                                        key={product.id} 
                                        className="group cursor-pointer"
                                        onClick={() => setQuickViewProduct(product)}
                                    >
                                        <div className="relative aspect-[3/4] overflow-hidden bg-slate-100 dark:bg-slate-800 rounded-sm">
                                            <img alt={product.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                src={mainImage} />
                                            <div className="absolute top-2 left-2 flex flex-col gap-1">
                                                {product.bestSeller && (
                                                    <span className="bg-amber-500 text-white text-[8px] font-black px-1.5 py-0.5 uppercase tracking-widest self-start">
                                                        Best Seller
                                                    </span>
                                                )}
                                                <span className="bg-primary text-white text-[8px] font-black px-1.5 py-0.5 uppercase tracking-widest self-start">
                                                    {styleBadge}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mt-2 space-y-0.5">
                                            <div className="flex justify-between items-start">
                                                <h3
                                                    className="font-bold text-slate-900 dark:text-white uppercase tracking-tighter text-[11px] leading-tight truncate pr-2">
                                                    {product.title}
                                                </h3>
                                                <p className="font-display font-bold text-primary text-[11px]">₹{(product.price / 100).toFixed(2)}</p>
                                            </div>
                                            <p className="text-slate-500 dark:text-slate-400 text-[10px] truncate">{product.description}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {visibleCount < displayedProducts.length && (
                        <div className="mt-12 text-center">
                            <button
                                onClick={handleLoadMore}
                                className="border border-slate-900 dark:border-white text-slate-900 dark:text-white px-8 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-black transition-all">
                                Load More Styles
                            </button>
                        </div>
                    )}
                </section>
            </main>

            {/* Quick View Modal (Lightbox) */}
            <ProductQuickView 
                product={quickViewProduct} 
                attributes={productsData.attributes}
                onClose={() => setQuickViewProduct(null)} 
            />
        </>
    );
}